import store from '@/app/store/store';
import { userApi } from '@/features/user/userApi';
import { IS_PROD } from '@/utils/environment';
import { firestore } from '@/utils/firebase';
import { addDoc, collection, Timestamp } from 'firebase/firestore';

const USER_ERROR_MESSAGES = {
	400: 'The request could not be processed. Please check your input and try again.',
	401: 'Your session has expired. Please sign in again.',
	403: 'You do not have permission to perform this action.',
	404: 'The requested resource could not be found.',
	500: 'A server error occurred. Please try again later.',
};

const DEFAULT_USER_ERROR_MESSAGE = 'Something went wrong. Please try again.';

export const getSafeUserErrorMessage = (error, fallback = DEFAULT_USER_ERROR_MESSAGE) => {
	const status = error?.status ?? error?.originalStatus ?? error?.response?.status;

	if (status && USER_ERROR_MESSAGES[status]) {
		return USER_ERROR_MESSAGES[status];
	}

	if (typeof status === 'number') {
		if (status >= 500) return USER_ERROR_MESSAGES[500];
		if (status >= 400) return USER_ERROR_MESSAGES[400];
	}

	return fallback;
};

export const parseRouteErrorData = (error) => {
	const raw = error?.data;

	if (raw == null || raw === '') {
		return {};
	}

	if (typeof raw === 'object') {
		return raw;
	}

	if (typeof raw === 'string') {
		try {
			return JSON.parse(raw);
		} catch {
			return { message: raw };
		}
	}

	return {};
};

export const extractRejectionReason = (error) => {
	return parseRouteErrorData(error)?.reason;
};

const MAX_LOG_FIELD_LENGTH = 8000;

const truncate = (value, maxLength = MAX_LOG_FIELD_LENGTH) => {
	if (typeof value !== 'string') {
		return value;
	}

	return value.length > maxLength ? value.slice(0, maxLength) : value;
};

const safeSerialize = (value) => {
	try {
		return JSON.parse(
			JSON.stringify(value, (_key, nestedValue) => {
				if (nestedValue instanceof Error) {
					return {
						name: nestedValue.name,
						message: nestedValue.message,
						stack: nestedValue.stack,
					};
				}

				return nestedValue;
			}),
		);
	} catch {
		return String(value);
	}
};

export const normalizeApplicationError = (error) => {
	if (error == null) {
		return {
			message: 'Unknown error',
			stack: '',
			componentStack: '',
			details: null,
		};
	}

	if (typeof error === 'string') {
		return {
			message: error,
			stack: '',
			componentStack: '',
			details: null,
		};
	}

	if (error instanceof Error) {
		return {
			message: error.message || error.name || 'Error',
			stack: error.stack ?? '',
			componentStack: error.componentStack ?? '',
			details: null,
		};
	}

	if (typeof error === 'object') {
		const routeData = parseRouteErrorData(error);
		const message =
			(typeof error.message === 'string' && error.message) ||
			(typeof error.statusText === 'string' && error.statusText) ||
			(typeof routeData?.reason === 'string' && routeData.reason) ||
			(typeof routeData?.message === 'string' && routeData.message) ||
			(typeof error.context === 'string' && error.context) ||
			'Unknown error';

		const { message: _message, stack, componentStack, ...rest } = error;

		return {
			message,
			stack:
				(typeof stack === 'string' && stack) ||
				(error.error instanceof Error ? error.error.stack : '') ||
				'',
			componentStack: typeof componentStack === 'string' ? componentStack : '',
			details: safeSerialize({
				...rest,
				routeData,
				nestedError:
					error.error instanceof Error
						? {
								name: error.error.name,
								message: error.error.message,
								stack: error.error.stack,
							}
						: error.error,
			}),
		};
	}

	return {
		message: String(error),
		stack: '',
		componentStack: '',
		details: null,
	};
};

const getLoggedInUserContext = () => {
	const state = store.getState();
	const account = state.msalAccount?.account;
	const email = account?.username;

	if (!email) return {};

	const userDetails = email
		? userApi.endpoints.getUserDetails.select({ email })(state)?.data
		: undefined;
	return {
		email,
		name: account?.name ?? null,
		msalLocalAccountId: account?.localAccountId ?? null,
		userId: userDetails?.userId ?? null,
		hubspotId: userDetails?.hubspotId ?? null,
		brokerGroupArray: userDetails?.brokerGroupArray ?? null,
	};
};

const logToFirebase = async (payload = {}) => {
	if (!IS_PROD) return;
	try {
		const user = getLoggedInUserContext();
		await addDoc(collection(firestore, 'errorLogging'), {
			...payload,
			user,
			...(user?.userId && { userId: user.userId }),
			createdAt: Timestamp.now(),
		});
	} catch (e) {
		console.error('Failed to report client error to Firebase', e);
	}
};

export const logApplicationError = (context = '', error) => {
	const normalized = normalizeApplicationError(error);

	const payload = {
		context,
		message: truncate(normalized.message),
		stack: truncate(normalized.stack),
		componentStack: truncate(normalized.componentStack),
		details: normalized.details,
		url: window.location.href ?? '',
		pathname: window.location.pathname ?? '',
	};

	if (IS_PROD) {
		logToFirebase(payload);
	} else {
		console.error(context, payload, error);
	}
};
